import React, { useEffect, useState } from 'react';
import { Col, Collapse, Divider, Row, Tooltip, Typography } from 'antd';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Formik } from 'formik';
import { Form, FormItem, ResetButton, Select, SubmitButton, Switch } from 'formik-antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import isEmpty from 'lodash/isEmpty';
import { Link, useSearchParams } from 'react-router';
import * as Yup from 'yup';
import { DatePicker, IntlMessages } from '@/components';
import { DEFAULT_RELEASE_TIME } from '../../../Featured/forms/FormEditFeature/FormEditFeature.const';
import getConfigurations from '../../../../lib/getConfigurations';
import useModifiedDynamicDataErrors from '../../../../app/hooks/useModifiedDynamicDataErrors';
import mkMainAndOptionalTemplateFields from '../../../TemplateBuilder/utils/mkMainAndOptionalTemplateFields';
import isFieldRequired from '../../utils/isFieldRequired';
import modifyDefaultValue from '../../../TemplateBuilder/utils/modifyDefaultValue';
import mkTemplateDynamicFormData from '../../utils/mkTemplateDynamicFormData';
import makeSelectFilterOption from '../../../../lib/makeSelectFilterOption';
import makeTemplatesQueryParams from '../../../../lib/makeTemplatesQueryParams';
import getSelectOptions from '../../../../lib/getSelectOptions';
import applyPreset from '../../../../lib/applyPreset';
import getCategoriesOptions from '../../../../lib/getCategoriesOptions';
import getSelectValues from '../../../../lib/getSelectValues';
import Spinner from '../../../../components/Spinner';
import TitleWithAutomaticGeneration from '../../../../components/TitleWithAutomaticGeneration';
import PresetPreview from '../../../../components/PresetPreview';
import DynamicFormDataFields from '../../components/DynamicFormDataFields';
import DynamicFormDataFieldsCollapse from '../../../../components/DynamicFormDataFieldsCollapse';
import { TIME_FORMAT } from '../../../../const/system';
import {
  fieldTypeToValidationRule,
  TEMPLATE_CONTENT_TYPES_MAP,
  TYPE_IMAGE,
  TYPE_OBJECT,
} from '../../../TemplateBuilder/const/templates';
import { COLLECTION_TYPES } from '../../pages/Collections/PageCollections/PageCollections.const';
import getEventTypeOptions from '../../../../lib/getEventTypeOptions';
import NullableTargetingSettings from '../../../../components/NullableTargetingSettings';
import { SOURCES_MAP } from '../../../../const/sources';
import dayjs from '../../../../lib/dayjsExtended';
import { makeValidationSchema } from './FormCreateCollection.const';
import { useGetCategoriesQuery } from '../../api/categoriesApiSlice';
import { useGetTemplatesQuery } from '../../../TemplateBuilder/api/templatesApiSlice';
import { useGetPresetsQuery } from '../../../TemplateBuilder/api/presetsApiSlice';

const { Title } = Typography;

let validation = {};

const FormCreateCollection = ({ initialErrors, onSubmit, isSubmitting }) => {
  const intl = useIntl();
  const collectionConfig = getConfigurations(SOURCES_MAP.COLLECTIONS);
  const language = useSelector(({ customise: { language } }) => language);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [searchQueryParams] = useSearchParams();

  const defaultConfigUtcValue = collectionConfig?.publish?.enabled_utc0_field ?? false;
  const isDisabledUtcField = !collectionConfig?.publish?.show_utc0_field;
  const useDatePickerTime = collectionConfig?.publish?.use_time ?? false;
  const hasReleaseRange = collectionConfig?.release_range ?? false;
  const useAutomaticGeneration = collectionConfig?.title?.use_automatic_generation ?? true;
  const imageTypes = collectionConfig?.image_types ?? [];

  const navigationSource = searchQueryParams.get('source') ?? null;

  const { data: templates = { data: [] }, isFetching: isTemplatesFetching } = useGetTemplatesQuery({
    queryParams: makeTemplatesQueryParams('status', TEMPLATE_CONTENT_TYPES_MAP.COLLECTION),
  });

  const { data: categories = { data: [] }, isLoading: isCategoriesLoading } = useGetCategoriesQuery({
    queryParams: 'limit=0',
  });
  const categoriesOptions = getCategoriesOptions(categories.data);
  const imageTypeOptions = getSelectValues(imageTypes);
  const collectionTypeOptions = getEventTypeOptions(COLLECTION_TYPES, intl);

  const { data: { data: presets } = { data: [] }, isFetching: isPresetsFetching } = useGetPresetsQuery(
    {
      queryParams: `search=template_id:${currentTemplate}`,
    },
    { skip: !currentTemplate },
  );

  const template = templates?.data?.find((template) => Number(template.id) === Number(currentTemplate));
  const modifiedErrors = useModifiedDynamicDataErrors(currentTemplate, template, initialErrors, language);
  const { mainFields, optionalFields } = mkMainAndOptionalTemplateFields(template?.fields?.data);

  useEffect(() => {
    validation = makeValidationSchema(collectionConfig, {});
  }, [isPresetsFetching]);

  const applyTemplate = async (value, setFieldValue) => {
    setCurrentTemplate(value);
    await setFieldValue('preset_id', null);
    await setFieldValue('template_id', value);

    if (!value) {
      setFieldValue('data', {});
      return;
    }

    const data = {};
    const dataSchema = {};
    const template = templates?.data?.find((template) => template.id === value);

    template?.fields?.data
      .filter((field) => field.type !== TYPE_OBJECT)
      .forEach((field) => {
        if (isFieldRequired(field?.validations?.data || [])) {
          dataSchema[field.full_name] = Yup.object().shape({
            template_field_id: Yup.number().required(),
            ...fieldTypeToValidationRule[field.type],
          });
        }

        data[field.full_name] = {
          value: field.type === TYPE_IMAGE ? [] : modifyDefaultValue(field),
          template_field_id: field.id,
        };
      });

    setFieldValue('data', data);
    setFieldValue('target', {
      min_version: template?.target?.data?.min_version,
      max_version: template?.target?.data?.max_version,
      platform: template?.target?.data?.platform,
      countries: template?.target?.data?.countries,
      languages: template?.target?.data?.languages,
    });

    validation = makeValidationSchema(collectionConfig, {
      data: Yup.object().shape(dataSchema),
    });
  };

  const initialValues = {
    title: null,
    categories: [],
    image_type: null,
    use_utc_zero_time: defaultConfigUtcValue,
    released_at: null,
    ...(hasReleaseRange && {
      released_to: null,
    }),
    template_id: null,
    preset_id: null,
    target: {
      min_version: null,
      max_version: null,
      platform: [],
      countries: [],
      languages: [],
    },
    data: {},
    type: navigationSource,
  };

  return (
    <Formik
      enableReinitialize
      isSubmitting={isSubmitting}
      initialErrors={modifiedErrors}
      initialValues={initialValues}
      validationSchema={validation}
      onSubmit={async (values, { resetForm }) => {
        onSubmit(mkTemplateDynamicFormData(values, language, template?.fields?.data), resetForm);
      }}
    >
      {(formikProps) => {
        const { isValid, dirty, values, setFieldValue, setFieldTouched, resetForm } = formikProps;

        const preset = presets?.find((preset) => Number(preset.id) === Number(values?.preset_id));

        return (
          <Form layout="vertical">
            <Spinner spinning={isSubmitting}>
              <Title className="hp-mb-24" level={4}>
                {intl.formatMessage({ id: 'collection-details-title' })}
              </Title>

              <Row gutter={16}>
                <Col md={12} xs={24}>
                  <Row gutter={16}>
                    <Col span={24}>
                      <FormItem label={intl.formatMessage({ id: 'ui-general-template' })} name="template_id" required>
                        <Select
                          loading={isTemplatesFetching}
                          allowClear
                          showSearch={{
                            filterOption: (input, option) => makeSelectFilterOption(option.label, input),
                          }}
                          name="template_id"
                          placeholder={intl.formatMessage({ id: 'ui-general-choose-template' })}
                          options={getSelectOptions(templates.data)}
                          onChange={async (value) => {
                            await applyTemplate(value, setFieldValue);
                          }}
                        />
                      </FormItem>
                    </Col>

                    <Col span={24}>
                      <FormItem
                        label={intl.formatMessage({ id: 'ui-general-preset' })}
                        extra={intl.formatMessage({ id: 'presets-template-hint' })}
                        name="preset_id"
                      >
                        <Select
                          loading={isPresetsFetching}
                          allowClear
                          showSearch={{
                            filterOption: (input, option) => makeSelectFilterOption(option.label, input),
                          }}
                          disabled={!currentTemplate}
                          name="preset_id"
                          placeholder={intl.formatMessage({ id: 'ui-general-choose-preset' })}
                          options={getSelectOptions(presets)}
                          onChange={async (value) => {
                            await applyPreset(value, values, setFieldValue, presets, template?.fields?.data ?? []);
                          }}
                        />
                      </FormItem>
                    </Col>

                    <Col span={24}>
                      <FormItem label={intl.formatMessage({ id: 'collection-form-categories-title' })} name="categories" required>
                        <Select
                          loading={isCategoriesLoading}
                          mode="multiple"
                          maxTagCount="responsive"
                          allowClear
                          showSearch={{
                            filterOption: (input, option) => makeSelectFilterOption(option.label, input),
                          }}
                          name="categories"
                          placeholder={intl.formatMessage({ id: 'ui-general-choose-categories' })}
                          options={categoriesOptions}
                        />
                      </FormItem>
                    </Col>

                    <Col span={24}>
                      <FormItem label={intl.formatMessage({ id: 'collection-form-collection-type-title' })} name="type" required>
                        <Select
                          allowClear
                          showSearch
                          name="type"
                          disabled={navigationSource}
                          placeholder={intl.formatMessage({ id: 'collection-form-choose-collection-type' })}
                          options={collectionTypeOptions}
                        />
                      </FormItem>
                    </Col>

                    <Col span={24}>
                      <FormItem label={intl.formatMessage({ id: 'collection-form-image-type-title' })} name="image_type" required>
                        <Select
                          allowClear
                          name="image_type"
                          options={imageTypeOptions}
                          placeholder={intl.formatMessage({ id: 'collection-form-specify-image-type' })}
                        />
                      </FormItem>
                    </Col>

                    {hasReleaseRange ? (
                      <>
                        <Col span={24}>
                          <FormItem label={intl.formatMessage({ id: 'ui-general-released-at' })} name="released_at">
                            <DatePicker
                              name="released_at"
                              showToday
                              showTime={useDatePickerTime ? { defaultValue: dayjs(DEFAULT_RELEASE_TIME, TIME_FORMAT) } : false}
                              disabledDate={(date) =>
                                (!isEmpty(values?.released_to) && date.isAfter(dayjs(values?.released_to))) ||
                                dayjs(date).subtract('-2', 'days').isSameOrBefore(dayjs(new Date()))
                              }
                              fromFormikValue={(value) => (value ? dayjs(value) : null)}
                              toFormikValue={(dateString) => dateString}
                            />
                          </FormItem>
                        </Col>
                        <Col span={24}>
                          <FormItem label={intl.formatMessage({ id: 'ui-general-released-to' })} name="released_to">
                            <DatePicker
                              name="released_to"
                              showTime={useDatePickerTime ? { defaultValue: dayjs(DEFAULT_RELEASE_TIME, TIME_FORMAT) } : false}
                              disabledDate={(date) => !isEmpty(values?.released_at) && date.isBefore(dayjs(values?.released_at))}
                              fromFormikValue={(value) => (value ? dayjs(value) : null)}
                              toFormikValue={(dateString) => dateString}
                            />
                          </FormItem>
                        </Col>
                      </>
                    ) : (
                      <Col span={24}>
                        <FormItem label={intl.formatMessage({ id: 'ui-general-released-at' })} name="released_at" required>
                          <DatePicker
                            name="released_at"
                            showToday
                            showTime={useDatePickerTime ? { defaultValue: dayjs(DEFAULT_RELEASE_TIME, TIME_FORMAT) } : false}
                            disabledDate={(date) => dayjs(date).subtract('-2', 'days').isSameOrBefore(dayjs(new Date()))}
                            fromFormikValue={(value) => (value ? dayjs(value) : null)}
                            toFormikValue={(dateString) => dateString}
                          />
                        </FormItem>
                      </Col>
                    )}

                    <Col span={24}>
                      <Collapse
                        accordion
                        className="hp-mb-24"
                        items={[
                          {
                            key: 'additional-info',
                            label: (
                              <Title className="collapse-item-title" level={5}>
                                {intl.formatMessage({ id: 'ui-general-optional-data' })}
                              </Title>
                            ),
                            children: (
                              <Row gutter={16}>
                                <Col span={24}>
                                  <TitleWithAutomaticGeneration rows={2} useAutomaticGeneration={useAutomaticGeneration} />
                                </Col>

                                <Col span={24}>
                                  <FormItem
                                    name="use_utc_zero_time"
                                    label={
                                      <Row className="w-full" justify="space-between" align="middle">
                                        <IntlMessages id="ui-general-use-utc" />
                                        <Tooltip
                                          placement="top"
                                          title={
                                            <>
                                              <IntlMessages id="ui-general-use-utc-info" />
                                              <div>
                                                <Link
                                                  to="https://x-flow-ltd.atlassian.net/wiki/spaces/HCD/pages/734461960/Use+UTC+0"
                                                  target="_blank"
                                                >
                                                  <u>
                                                    <IntlMessages id="ui-general-documentation-link" />
                                                  </u>
                                                </Link>
                                              </div>
                                            </>
                                          }
                                        >
                                          <InfoCircleOutlined className="hp-ml-8" />
                                        </Tooltip>
                                      </Row>
                                    }
                                  >
                                    <Switch
                                      name="use_utc_zero_time"
                                      defaultChecked={defaultConfigUtcValue}
                                      disabled={isDisabledUtcField}
                                    />
                                  </FormItem>
                                </Col>
                              </Row>
                            ),
                          },
                        ]}
                      />
                    </Col>
                  </Row>
                </Col>
                <Col md={12} xs={24}>
                  <PresetPreview src={preset?.preview} />
                </Col>
              </Row>

              {values?.template_id && (
                <>
                  <Divider />
                  <Title className="hp-mb-24" level={4}>
                    <IntlMessages id="ui-general-template-data" />
                  </Title>
                  <Row gutter={[16, 16]} className="hp-mb-24">
                    <Col span={12}>
                      <DynamicFormDataFields formikProps={formikProps} fields={mainFields} />
                      <DynamicFormDataFieldsCollapse optionalFields={optionalFields} formikProps={formikProps} />
                    </Col>
                  </Row>

                  <Divider />

                  <NullableTargetingSettings setFieldValue={setFieldValue} setFieldTouched={setFieldTouched} values={values} />
                </>
              )}

              <Row gutter={[16, 16]} className="hp-mt-16" justify="end">
                <Col>
                  <ResetButton
                    disabled={false}
                    onClick={() => {
                      setCurrentTemplate(null);
                      resetForm(initialValues);
                    }}
                  >
                    <IntlMessages id="ui-general-reset" />
                  </ResetButton>
                </Col>

                <Col>
                  <SubmitButton type="primary" loading={isSubmitting} disabled={!isValid || !dirty}>
                    <IntlMessages id="ui-general-save" />
                  </SubmitButton>
                </Col>
              </Row>
            </Spinner>
          </Form>
        );
      }}
    </Formik>
  );
};

FormCreateCollection.propTypes = {
  initialErrors: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
};

export default FormCreateCollection;
